import { trim } from 'lodash';
import { action, observable } from 'mobx';
import {
    createGroup,
    getDefaultGroup,
    getGroupById,
    getGroupPermissions,
    getGroupsGroupMembers,
    getGroupsParentGroups,
    updateGroup,
    updateGroupMembers,
    updateGroupParentGroups,
    updateGroupPermissions
} from '../AppShell/Authentication/AuthorisationAsync';
import {
    GroupICreateNewGroup,
    GroupIEditGroup,
    GroupIGroup,
    GroupIGroupMember,
    GroupIGroupPermissionsUpdate,
    IInvalidGroup,
    isUnprocessableInvalidGroups
} from '../AppShell/Authentication/AuthorisationModels';
import { displayMessage } from '../Config/ApplicationConfig';
import { defaultAuthoringSystem } from '../Shared/Constants';
import { IGuidResult } from '../Shared/HttpRequestExecutor';
import { Maybe } from '../Shared/Types';

export type SaveGroupInfoResults = {
    info: boolean | IInvalidGroup[];
    id: string;
    parents: boolean | IInvalidGroup[];
    members: boolean | IInvalidGroup[];
};

export class GroupFileStore {
    public static getMembersForUpdate(id: string, members: GroupIGroupMember[]) {
        return {
            groupId: id,
            members: members.map((member: GroupIGroupMember) => {
                return {
                    id: member.id,
                    authoringSystem: member.authoringSystem,
                    authoringSystemForeignKey: member.authoringSystemForeignKey
                };
            })
        };
    }

    public static getParentGroupsForUpdate(id: string, group: GroupIGroup) {
        return {
            permissionOwnerId: id,
            authoringSystem: group.authoringSystem,
            authoringSystemForeignKey: group.authoringSystemForeignKey,
            groups: group.parentGroups.map(pg => ({
                id: pg.id,
                name: pg.name,
                authoringSystem: pg.authoringSystem,
                authoringSystemForeignKey: pg.authoringSystemForeignKey
            }))
        };
    }
    @observable public formError = '';

    constructor() {}

    @action
    public setFormError = (error: string) => {
        this.formError = error;
    };

    public loadGroupInformation =
        (groupId?: string) =>
        async (): Promise<{
            group: Maybe<GroupIGroup>;
            defaultGroup: Maybe<GroupIGroup>;
        }> => {
            try {
                const [group, parentGroups, groupMembers, defaultGroup] = await Promise.all([
                    groupId ? getGroupById(groupId) : Promise.resolve(undefined),
                    groupId ? getGroupsParentGroups(groupId) : [],
                    groupId ? getGroupsGroupMembers(groupId) : [],
                    this.loadDefaultGroup()
                ]);

                if (group) {
                    group.parentGroups = parentGroups ?? [];
                    group.members = groupMembers ?? [];
                }

                return {
                    group,
                    defaultGroup
                };
            } catch (err) {
                console.error('Failed to load group: ', err);
                if (typeof err === 'string') {
                    this.setFormError(err);
                }
                throw new Error('Unabled to load group information');
            }
        };

    public saveGroupInformation =
        (groupId?: string) =>
        async (group: GroupIGroup): Promise<SaveGroupInfoResults> => {
            this.setFormError('');
            const saveStatus: SaveGroupInfoResults = {
                info: false,
                id: '',
                parents: false,
                members: false
            };

            let errorMessage: string = '';

            if (!groupId) {
                const newGroup: GroupICreateNewGroup = {
                    username: trim(group.username),
                    name: trim(group.name),
                    enabled: group.enabled,
                    isDefaultGroup: group.isDefaultGroup,
                    authoringSystem: defaultAuthoringSystem,
                    authoringSystemForeignKey: group.authoringSystemForeignKey
                };

                try {
                    const result: IGuidResult = await createGroup(newGroup);

                    groupId = result.id;
                    saveStatus.info = true;
                    saveStatus.id = result.id;
                } catch (err) {
                    if (typeof err === 'string') {
                        this.setFormError(err);
                    }

                    return saveStatus;
                }

                try {
                    const parentGroups = GroupFileStore.getParentGroupsForUpdate(groupId, group);
                    await updateGroupParentGroups(groupId, parentGroups);

                    saveStatus.parents = true;
                } catch (err) {
                    console.error('Unable to update parents - err: ', err);
                    if (isUnprocessableInvalidGroups(err)) {
                        errorMessage = err.message;
                        saveStatus.parents = err.content;
                    } else {
                        if (typeof err === 'string') {
                            errorMessage = err;
                        }
                        saveStatus.parents = false;
                    }
                }

                try {
                    const memberUpdate = GroupFileStore.getMembersForUpdate(groupId, group.members);
                    await updateGroupMembers(groupId, memberUpdate);

                    saveStatus.members = true;
                } catch (err) {
                    console.error('Unable update memebers - err: ', err);
                    if (isUnprocessableInvalidGroups(err)) {
                        errorMessage = err.message;
                        saveStatus.members = err.content;
                    } else {
                        if (typeof err === 'string') {
                            errorMessage = err;
                        }
                        saveStatus.members = false;
                    }
                }

                if (errorMessage) this.setFormError(errorMessage);
                else displayMessage('Group created');
            } else {
                const updateForGroup: GroupIEditGroup = {
                    username: trim(group.username),
                    name: trim(group.name),
                    enabled: group.enabled,
                    isDefaultGroup: group.isDefaultGroup,
                    authoringSystem: group.authoringSystem,
                    authoringSystemForeignKey: group.authoringSystemForeignKey
                };

                try {
                    await updateGroup(groupId, updateForGroup);

                    saveStatus.info = true;
                    saveStatus.id = groupId;
                } catch (err) {
                    if (typeof err === 'string') {
                        errorMessage = err;
                    }
                    saveStatus.info = false;
                }

                try {
                    const parentGroups = GroupFileStore.getParentGroupsForUpdate(groupId, group);
                    await updateGroupParentGroups(groupId, parentGroups);

                    saveStatus.parents = true;
                } catch (err) {
                    if (isUnprocessableInvalidGroups(err)) {
                        errorMessage = err.message;
                        saveStatus.parents = err.content;
                    } else {
                        if (typeof err === 'string') {
                            errorMessage = err;
                        }
                        saveStatus.parents = false;
                    }
                }

                try {
                    const memberUpdate = GroupFileStore.getMembersForUpdate(groupId, group.members);
                    await updateGroupMembers(groupId, memberUpdate);

                    saveStatus.members = true;
                } catch (err) {
                    if (isUnprocessableInvalidGroups(err)) {
                        errorMessage = err.message;
                        saveStatus.members = err.content;
                    } else {
                        if (typeof err === 'string') {
                            errorMessage = err;
                        }
                        saveStatus.members = false;
                    }
                }

                if (errorMessage) this.setFormError(errorMessage);
                else displayMessage('Group updated');
            }

            return saveStatus;
        };

    public loadPermissions = (groupId?: string) => async () => {
        try {
            return await getGroupPermissions(groupId || '');
        } catch (err) {
            console.error('Failed to load permissions: ', err);
            if (typeof err === 'string') {
                this.setFormError(err);
            }
        }
    };

    public updatePermissions =
        (groupId: string) =>
        async (permissions: GroupIGroupPermissionsUpdate): Promise<boolean> => {
            try {
                await updateGroupPermissions(groupId, permissions);

                displayMessage('Group permissions updated');
                return true;
            } catch (err) {
                console.error('Failed to update permissions - err: ', err);
                if (typeof err === 'string') {
                    this.setFormError(err);
                }
                return false;
            }
        };

    public loadDefaultGroup = async (): Promise<GroupIGroup | undefined> => {
        try {
            return await getDefaultGroup();
        } catch (err) {
            console.error('Failed to get default group = ', err);
            return;
        }
    };
}
